import { useEffect, useRef } from 'react';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import {
  initPostHog,
  isPostHogConfigured,
  posthog,
  resetPostHogIdentityForLogout,
  setPostHogConsent
} from '@lib/posthog';
import { registerUtmSuperProperties } from '@lib/analytics/posthog-utm';
import { registerSharedSuperProperties } from '@lib/analytics/posthog-super-properties';
import PostHogPageviewTracker from '@components/PostHogPageviewTracker';
import { useAuth0User } from '@hooks/useAuth0User';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { useProfile } from '@hooks/useProfile';

interface PostHogConsentProps {
  hasAnalyticsConsent: boolean;
  /** True once the initial CookieYes read has completed (consent.hasChecked). */
  hasConsentBeenChecked: boolean;
  /**
   * True once MSW and any Cypress overrides are installed (mocksReady in
   * _app); always true from the first render in production. This provider
   * deliberately mounts OUTSIDE the mocksReady gate so flags resolve early,
   * so its league-context query must respect the gate itself or it fires
   * /league/context before worker.use() runs and poisons the React Query
   * cache with the default response.
   */
  isLeagueContextEnabled: boolean;
}

const PostHogSetup: React.FC<PostHogConsentProps> = ({
  hasAnalyticsConsent,
  hasConsentBeenChecked,
  isLeagueContextEnabled
}) => {
  useEffect(() => {
    // Always init so feature flags resolve; capture stays opted-out until
    // consent (see initPostHog / setPostHogConsent in @lib/posthog).
    initPostHog();
    // FFH-1899: attach the UTM split (last-touch utm_*, first-touch
    // first_utm_*) from the first-party cookies to every event. Registering
    // captures nothing by itself, so it is safe pre-consent.
    registerUtmSuperProperties();
  }, []);

  // Apply consent here (not from the orchestrator) so it runs after init and
  // re-applies whenever consent flips — grant enables capture + localStorage,
  // revoke opts out + resets + drops to memory persistence.
  useEffect(() => {
    // FFH-2368: the orchestrator starts every load in a pessimistic denied
    // state until the ~500ms CookieYes settle. Applying that placeholder would
    // run the revoke branch (reset() plus the anonymous-cookie clear) on
    // every hard load, destroying the identity continuity this provider
    // exists to keep. Only a consent value that has actually been read from
    // CookieYes is allowed through.
    if (!hasConsentBeenChecked) return;
    setPostHogConsent(hasAnalyticsConsent);
    // FFH-1899: a revoke resets PostHog, wiping super properties — re-attach
    // the UTM split when consent is (re-)granted.
    if (hasAnalyticsConsent) {
      registerUtmSuperProperties();
    }
  }, [hasConsentBeenChecked, hasAnalyticsConsent]);

  const { user, error, isLoading } = useAuth0User();
  const {
    profile,
    isLoading: isProfileLoading,
    isError: isProfileError
  } = useProfile();
  const hasUser = Boolean(user);
  const profileId = profile?.id;
  // FFH-2368: tracks whether we have actually seen a signed-in user, so a
  // logout can be told apart from "not signed in yet".
  const wasSignedInRef = useRef(false);
  useEffect(() => {
    if (!isPostHogConfigured()) return;
    // A failed or in-flight profile fetch also yields user undefined (SWR
    // revalidates on window focus, so a network blip mid-session looks
    // exactly like a sign-out). Auth state is unknown here, not logged out,
    // so change nothing until the hook settles cleanly.
    if (isLoading || error) return;
    // FFH-2368: track the signed-in state BEFORE the consent guard, so a
    // sign-in seen while consent was denied still counts as signed in once
    // consent is granted later in the session.
    const wasSignedIn = wasSignedInRef.current;
    wasSignedInRef.current = hasUser;
    // No identify or reset without analytics consent. Depending on
    // hasAnalyticsConsent means this re-runs when consent is granted after
    // load, identifying the already-loaded user; on revoke setPostHogConsent()
    // has already reset().
    if (!hasAnalyticsConsent) return;
    if (!hasUser) {
      // FFH-2368: reset ONLY on a genuine logout transition. Resetting
      // whenever there is no user minted a new anonymous distinct_id on every
      // hard page load, so each load became a new person and anonymous visitor
      // counts ran ~25x high. Real logouts are full-document navigations
      // handled by the middleware (which expires the anonymous cookie on
      // /auth/logout); this branch covers in-SPA session expiry.
      if (!wasSignedIn) return;

      resetPostHogIdentityForLogout();
      // FFH-1899: reset() wipes super properties, so re-attach the UTM split or
      // every subsequent event this session goes out without utm_* properties.
      registerUtmSuperProperties();
      return;
    }
    // Identify on the backend profileId (a UUID), sourced from the profile like
    // CleverTap, so app, web, CleverTap and PostHog all key on the same id.
    // Reliable regardless of when Auth0 assigns the `profileId` token claim,
    // which is set asynchronously on login and can lag.
    if (profileId) {
      posthog.identify(profileId);
    }
  }, [hasUser, profileId, hasAnalyticsConsent, isLoading, error]);

  const { context: leagueContext } = useLeagueContext({
    enabled: isLeagueContextEnabled
  });
  // FFH-2371: attach the shared super properties (platform, membership_tier,
  // is_logged_in, has_fpl_id, gameweek) to every event. Registering captures
  // nothing by itself, so it is safe pre-consent. Declared AFTER the identity
  // effect on purpose: effects run in declaration order, so on a logout render
  // this re-register lands after resetPostHogIdentityForLogout()'s wipe, and
  // on a consent flip it lands after setPostHogConsent()'s reset — the same
  // ordering the UTM re-registration relies on.
  useEffect(() => {
    if (!isPostHogConfigured()) return;
    // Auth state is unknown while the Auth0 hook is loading or errored;
    // re-registering then would stamp is_logged_in: false on a member's
    // events mid-session. Keep the last known values until it settles.
    if (isLoading || error) return;
    // The same bug class for the profile fetch: for a signed-in member,
    // profile is null while /users/me resolves (or after it errors), so
    // registering then would stamp has_fpl_id: false on a member with a
    // linked team. Both flags are false for anonymous visitors (useProfile
    // scopes them to authenticated sessions), so this never blocks them.
    // Anonymous visitors also settle cleanly on the Auth0 side only because
    // /auth/profile answers 204 rather than 401 for a missing session
    // (`noContentProfileResponseWhenUnauthenticated` in lib/auth0.ts); a 401
    // surfaces as `error` above and would hold this register forever.
    if (isProfileLoading || isProfileError) return;
    registerSharedSuperProperties({ user, profile, leagueContext });
  }, [
    user,
    profile,
    leagueContext,
    hasAnalyticsConsent,
    hasConsentBeenChecked,
    isLoading,
    error,
    isProfileLoading,
    isProfileError
  ]);

  return null;
};

const PostHogProvider: React.FC<
  PostHogConsentProps & { children: React.ReactNode }
> = ({
  children,
  hasAnalyticsConsent,
  hasConsentBeenChecked,
  isLeagueContextEnabled
}) => {
  if (!isPostHogConfigured()) {
    return <>{children}</>;
  }

  return (
    <PHProvider client={posthog}>
      <PostHogSetup
        hasAnalyticsConsent={hasAnalyticsConsent}
        hasConsentBeenChecked={hasConsentBeenChecked}
        isLeagueContextEnabled={isLeagueContextEnabled}
      />
      <PostHogPageviewTracker
        hasAnalyticsConsent={hasAnalyticsConsent}
        hasConsentBeenChecked={hasConsentBeenChecked}
      />
      {children}
    </PHProvider>
  );
};

export default PostHogProvider;
